import { IOccupancyBreakdownDataPoint } from "@tether-web-portal/web-workers/occupancy/types/OccupancyBreakdown";
import { RoomAggregatedData } from "@tether-web-portal/web-workers/occupancy/types/OccupancyWorker";
import { PropertyDetail } from "@tether-web-portal/types/PropertyDetail";
import { RoomLocationAggregate } from "@tether-web-portal/types/RoomLocation";

import { detectRoomOccupancyMetric, getRoomBucketOccupancyCount } from "./metrics";

export interface OccupancyRoomDebugRow {
  roomId: string;
  roomName: string;
  capacity: number | undefined;
  /** Whether the room has a configured capacity (> 0). */
  capacityConfigured: boolean;
  bucketCount: number;
  /** Count of aggregate entries with a duplicate (startTimestamp, endTimestamp) key. */
  duplicateBucketCount: number;
  peakAllHours: number;
  peakAllHoursAt: string | null;
  /** Room's occupancy at the exact timestamp that produced maxBuildingBucket. */
  valueAtPeakBucket: number;
  metricSource: "regionOccupancy" | "occupancy" | "occupied" | "none" | "mixed";
}

export interface OccupancyTopBucketRow {
  startTimestamp: string;
  endTimestamp: string;
  total: number;
  /** Sum of per-room contributions — should equal `total`; otherwise we have a bug. */
  sumOfContributions: number;
  rooms: { roomId: string; roomName: string; count: number }[];
}

export interface OccupancyDiagnostics {
  roomRows: OccupancyRoomDebugRow[];
  topBuckets: OccupancyTopBucketRow[];
  summary: {
    /** The tile / Total row / summary pill number. max_t (Σ rooms). */
    maxBuildingBucket: number;
    /**
     * Σ rooms (max_t) across every room with data — both capacity-configured
     * and unconfigured. Cap=0 rooms are included in building aggregates per
     * the post-revert design (3f411b2e), so this number is the like-for-like
     * comparison against `maxBuildingBucket`.
     */
    sumOfRoomPeaks: number;
    /**
     * Back-compat alias for `sumOfRoomPeaks`. Kept so existing callers /
     * tests that spelled the old name still resolve. New code should read
     * `sumOfRoomPeaks`.
     */
    sumOfRoomPeaksAggregate: number;
    /**
     * Subset of `sumOfRoomPeaks` contributed by rooms without a configured
     * capacity. Informational — these rooms inflate the building total
     * relative to `% of capacity` because they appear in the numerator
     * but not the denominator. Surfaced on the page via the orange-triangle
     * warning on the Total Occupancy tile.
     */
    unconfiguredRoomPeaks: number;
    /** sumOfRoomPeaks - maxBuildingBucket — how much per-room peaks don't overlap in time. */
    staggeredPeakGap: number;
    sumOfRoomPeaksMonitored: number;
    totalDuplicateAggregates: number;
    invariantHolds: boolean;
  };
  warnings: string[];
}

const sampleMetricSource = (
  buckets: RoomLocationAggregate[],
): OccupancyRoomDebugRow["metricSource"] => {
  let seenRegion = false;
  let seenPeople = false;
  let seenOccupied = false;
  for (const b of buckets) {
    if (b.metrics.regionOccupancy) seenRegion = true;
    if (b.metrics.occupancy) seenPeople = true;
    if (b.metrics.occupied || b.metrics.occupiedPercentage) seenOccupied = true;
    if (seenRegion && seenPeople) return "mixed";
  }
  if (seenRegion) return "regionOccupancy";
  if (seenPeople) return "occupancy";
  if (seenOccupied) return "occupied";
  return "none";
};

export interface BuildDiagnosticsInput {
  propertyInfo: PropertyDetail;
  roomAggregations: RoomAggregatedData[];
  breakdownData: IOccupancyBreakdownDataPoint[];
  sumOfRoomPeaksMonitored: number;
}

export const buildOccupancyDiagnostics = (
  input: BuildDiagnosticsInput,
): OccupancyDiagnostics => {
  const { propertyInfo, roomAggregations, breakdownData, sumOfRoomPeaksMonitored } = input;

  // Find the building-wide peak and the timestamp at which it occurs. Spans
  // ALL buckets (monitored or not) because occupancy counts are not scoped
  // to monitoring hours — only usage %. The breakdownData includes every
  // contributing room (including cap=0 ones) per the post-3f411b2e design.
  let maxBuildingBucket = 0;
  let maxBuildingBucketAt: string | null = null;
  for (const b of breakdownData) {
    const count = b.breakdown.occupancyCount ?? 0;
    if (count > maxBuildingBucket) {
      maxBuildingBucket = count;
      maxBuildingBucketAt = b.startTimestamp;
    }
  }

  const roomRows: OccupancyRoomDebugRow[] = [];
  const warnings: string[] = [];
  let sumOfRoomPeaks = 0;
  let unconfiguredRoomPeaks = 0;
  let totalDuplicateAggregates = 0;

  for (const room of propertyInfo.rooms) {
    const entry = roomAggregations.find((x) => x?.roomId === room.id);
    const data = entry?.aggregatedData ?? [];
    if (!data.length) continue;

    // Single-pass dedupe + peak walk. Earlier this was an O(n²) `findIndex`
    // pass after an O(n) Set-based count — measurable on properties with
    // 30+ days of 15-min buckets and meaningful enough to slow filter
    // toggles. One pass suffices.
    const seenKeys = new Set<string>();
    let duplicateBucketCount = 0;
    const dedupedBuckets: RoomLocationAggregate[] = [];
    for (const b of data) {
      const key = `${b.startTimestamp}_${b.endTimestamp}`;
      if (seenKeys.has(key)) {
        duplicateBucketCount += 1;
        continue;
      }
      seenKeys.add(key);
      dedupedBuckets.push(b);
    }
    totalDuplicateAggregates += duplicateBucketCount;
    if (duplicateBucketCount > 0) {
      warnings.push(
        `Room "${room.name}" (${room.id}) received ${duplicateBucketCount} duplicate bucket(s) from the API. The model now dedupes these; heatmap/bar-chart values in earlier builds were inflated.`,
      );
    }

    // Match the model's per-room lock-in: decide the metric once, then read
    // only that metric from each bucket. Buckets missing the chosen metric
    // are "ignored" (counted as 0) — the same behaviour the model applies.
    const metricChoice = detectRoomOccupancyMetric(dedupedBuckets);

    let peakAllHours = 0;
    let peakAllHoursAt: string | null = null;
    let valueAtPeakBucket = 0;
    let bucketsMissingChosenMetric = 0;
    for (const bucket of dedupedBuckets) {
      const rawValue = getRoomBucketOccupancyCount(bucket, "max", metricChoice);
      if (rawValue === undefined) bucketsMissingChosenMetric += 1;
      const value = rawValue ?? 0;
      if (value > peakAllHours) {
        peakAllHours = value;
        peakAllHoursAt = bucket.startTimestamp;
      }
      if (maxBuildingBucketAt && bucket.startTimestamp === maxBuildingBucketAt) {
        valueAtPeakBucket = value;
      }
    }

    const capacityConfigured = (room.capacity ?? 0) > 0;
    sumOfRoomPeaks += peakAllHours;
    if (!capacityConfigured) unconfiguredRoomPeaks += peakAllHours;

    const metricSource = sampleMetricSource(data);

    if (!capacityConfigured && peakAllHours > 0) {
      warnings.push(
        `Room "${room.name}" (${room.id}) has ${peakAllHours} peak occupants but no configured capacity. People are counted in the Total Occupancy tile (so the headline number reflects reality), but the room contributes nothing to the capacity denominator — pushing the displayed % of capacity higher than it would be with the room properly configured. The Total Occupancy tile surfaces an orange-triangle warning listing this room.`,
      );
    }
    if (metricSource === "none" && peakAllHours > 0) {
      warnings.push(
        `Room "${room.name}" (${room.id}) reports occupancy but neither regionOccupancy nor occupancy was present on the API response.`,
      );
    }
    if (metricChoice === "region" && bucketsMissingChosenMetric > 0) {
      warnings.push(
        `Room "${room.name}" (${room.id}) is region-based but ${bucketsMissingChosenMetric} bucket(s) only reported \`occupancy\` (no regionOccupancy). Those buckets are ignored in totals per rule 4 to avoid mixing "zones occupied" with "people present".`,
      );
    }

    roomRows.push({
      roomId: room.id,
      roomName: room.name,
      capacity: room.capacity,
      capacityConfigured,
      bucketCount: dedupedBuckets.length,
      duplicateBucketCount,
      peakAllHours,
      peakAllHoursAt,
      valueAtPeakBucket,
      metricSource,
    });
  }

  // Top 10 buckets by total occupancy, with per-room contributions. Includes
  // ALL rooms (including cap=0) because the model itself does — otherwise the
  // diagnostic's `sumOfContributions` would spuriously diverge from `total`
  // wherever a cap=0 room contributed.
  const sortedByTotal = [...breakdownData].sort(
    (a, b) => (b.breakdown.occupancyCount ?? 0) - (a.breakdown.occupancyCount ?? 0),
  );
  const topBuckets: OccupancyTopBucketRow[] = sortedByTotal.slice(0, 10).map((bucket) => {
    const contributions: { roomId: string; roomName: string; count: number }[] = [];
    for (const room of propertyInfo.rooms) {
      const entry = roomAggregations.find((x) => x?.roomId === room.id);
      const match = entry?.aggregatedData.find((b) => b.startTimestamp === bucket.startTimestamp);
      if (!match) continue;
      const metricChoice = detectRoomOccupancyMetric(entry?.aggregatedData ?? []);
      const count = getRoomBucketOccupancyCount(match, "max", metricChoice) ?? 0;
      if (count > 0) contributions.push({ roomId: room.id, roomName: room.name, count });
    }
    contributions.sort((a, b) => b.count - a.count);
    const sumOfContributions = contributions.reduce((s, c) => s + c.count, 0);
    return {
      startTimestamp: bucket.startTimestamp,
      endTimestamp: bucket.endTimestamp,
      total: bucket.breakdown.occupancyCount ?? 0,
      sumOfContributions,
      rooms: contributions,
    };
  });

  // Σ(per-room peak) >= max_t(Σ rooms occupancy(t)) is mathematically
  // guaranteed when both sides span the same population of rooms. Since
  // 3f411b2e the model's `maxBuildingBucket` includes cap=0 rooms, so
  // `sumOfRoomPeaks` (also all rooms) is the like-for-like sum to compare
  // against. A violation now would indicate a real bug — typically a
  // double-counted bucket the dedupe missed.
  const invariantHolds = sumOfRoomPeaks >= maxBuildingBucket;
  if (!invariantHolds) {
    warnings.push(
      `INVARIANT VIOLATED: sumOfRoomPeaks (${sumOfRoomPeaks}) < maxBuildingBucket (${maxBuildingBucket}). Should be mathematically impossible — expand the Top buckets table to find which bucket sums higher than the constituent room peaks.`,
    );
  }

  const staggeredPeakGap = sumOfRoomPeaks - maxBuildingBucket;

  return {
    roomRows,
    topBuckets,
    summary: {
      maxBuildingBucket,
      sumOfRoomPeaks,
      sumOfRoomPeaksAggregate: sumOfRoomPeaks,
      unconfiguredRoomPeaks,
      staggeredPeakGap,
      sumOfRoomPeaksMonitored,
      totalDuplicateAggregates,
      invariantHolds,
    },
    warnings,
  };
};

/**
 * Window global used to opt in to verbose logging. Off by default because
 * `console.table` with 100+ rows triggers expensive DevTools serialisation
 * on every model rebuild — enough to freeze the page for seconds during
 * cold load on large properties. Turn on from the console with
 * `window.__occupancyDebug = true` to inspect per-room peaks, top buckets,
 * and warnings; the latest diagnostics snapshot is always exposed on
 * `window.__occupancyDiagnostics` regardless of the flag.
 */
interface OccupancyDebugWindow {
  __occupancyDebug?: boolean;
  __occupancyDiagnostics?: OccupancyDiagnostics;
}

export const logOccupancyDiagnostics = (diag: OccupancyDiagnostics): void => {
  // Always publish the latest snapshot so it can be inspected on demand
  // without re-rendering the page. Reading from this global is O(1) and
  // doesn't incur the DevTools serialisation cost of `console.table`.
  if (typeof window !== "undefined") {
    (window as unknown as OccupancyDebugWindow).__occupancyDiagnostics = diag;
  }

  const debugOn =
    typeof window !== "undefined" &&
    Boolean((window as unknown as OccupancyDebugWindow).__occupancyDebug);

  // Surface invariant violations unconditionally — they indicate a bug, not
  // just debug information.
  if (!diag.summary.invariantHolds) {
    // eslint-disable-next-line no-console
    console.warn(
      `[Occupancy] INVARIANT VIOLATED: sumOfRoomPeaks (${diag.summary.sumOfRoomPeaks}) < maxBuildingBucket (${diag.summary.maxBuildingBucket}). Run \`window.__occupancyDebug = true\` and reload to see the full breakdown.`,
    );
  }

  if (!debugOn) return;

  const hue = diag.summary.invariantHolds ? "#7133C3" : "#D00";
  // eslint-disable-next-line no-console
  console.groupCollapsed(
    `%c[Occupancy] diagnostics — ${diag.roomRows.length} rooms, ${diag.warnings.length} warnings${diag.summary.totalDuplicateAggregates ? `, ${diag.summary.totalDuplicateAggregates} duplicate aggregates` : ""}`,
    `color: ${hue}; font-weight: bold;`,
  );

  const s = diag.summary;
  // eslint-disable-next-line no-console
  console.log(
    [
      `maxBuildingBucket:        ${s.maxBuildingBucket}  (tile / Total row / summary pill number)`,
      `sumOfRoomPeaks:           ${s.sumOfRoomPeaks}  (Σ per-room max across every room with data — what Σ(Room row Max) adds up to)`,
      `unconfiguredRoomPeaks:    ${s.unconfiguredRoomPeaks}  (subset of sumOfRoomPeaks contributed by rooms with capacity = 0; same numbers feed the orange-triangle warning)`,
      `staggeredPeakGap:         ${s.staggeredPeakGap}  (how much per-room peaks don't overlap in time; = sumOfRoomPeaks - maxBuildingBucket)`,
      `sumOfRoomPeaksMonitored:  ${s.sumOfRoomPeaksMonitored}  (Σ per-room max within monitored hours)`,
      `invariantHolds:           ${s.invariantHolds}`,
    ].join("\n"),
  );

  // eslint-disable-next-line no-console
  console.log("Per-room peaks (matches the utilisation table's per-room Max column):");
  // eslint-disable-next-line no-console
  console.table(
    diag.roomRows.map((r) => ({
      room: r.roomName,
      id: r.roomId,
      cap: r.capacity ?? "—",
      capSet: r.capacityConfigured ? "✓" : "—",
      metric: r.metricSource,
      peakAll: r.peakAllHours,
      peakAllAt: r.peakAllHoursAt,
      atPeakBucket: r.valueAtPeakBucket,
      buckets: r.bucketCount,
      dupes: r.duplicateBucketCount,
    })),
  );

  // eslint-disable-next-line no-console
  console.log("Top 10 building buckets (what drives the heatmap's hottest cells, includes every contributing room):");
  // eslint-disable-next-line no-console
  console.table(
    diag.topBuckets.map((b) => ({
      start: b.startTimestamp,
      total: b.total,
      sumContrib: b.sumOfContributions,
      match: b.total === b.sumOfContributions ? "✓" : `✗ (Δ=${b.total - b.sumOfContributions})`,
      contributors: b.rooms.map((r) => `${r.roomName}:${r.count}`).join(", "),
    })),
  );

  if (diag.warnings.length) {
    // eslint-disable-next-line no-console
    console.warn("Warnings:");
    for (const w of diag.warnings) {
      // eslint-disable-next-line no-console
      console.warn("  • " + w);
    }
  }

  // eslint-disable-next-line no-console
  console.groupEnd();
};
